import { Spinner } from '@/components/glitch-ui/spinner';
import { useState } from 'react';
import {
  Copy,
  Trash2,
  MoreHorizontal,
  Pause,
  ExternalLink,
  ArrowRightLeft,
  ChevronDown,
  Plus,
  Archive,
  RotateCcw,
  Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { useNavigate } from '@tanstack/react-router';
import { useToast } from '@/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import { useCampaignActions } from '@/hooks/useCampaignActions';
import { CampaignData } from '@/lib/types';
import { downloadCampaignMarkdown } from '@/lib/export-campaign';
import useAreAllTasksCompleted from '@/hooks/useAreAllTasksCompleted';
import { useAtomValue } from 'jotai';
import { googleAdsAccountAtom, organizationDataAtom } from '@/store';

interface CampaignActionsDropdownProps {
  campaign: CampaignData;
  className?: string;
  onCopy?: () => void;
  onDelete?: () => void;
  onTransferCampaign?: () => void;
  onNewAd?: () => void;
  newAdDisabled?: boolean;
  variant?: 'table' | 'header';
  viewInGoogleAdsAction?: {
    onClick: () => void;
    disabled?: boolean;
    disabledReason?: string;
  };
}

export const CampaignActionsDropdown = ({
  campaign,
  className = '',
  onCopy,
  onDelete,
  onTransferCampaign,
  onNewAd,
  newAdDisabled,
  variant = 'table',
  viewInGoogleAdsAction,
}: CampaignActionsDropdownProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const { actions, states } = useCampaignActions({ campaign, onCopy, onDelete });
  const { isLoading: isTasksLoading, areCompleted } = useAreAllTasksCompleted();
  const isStillGenerating = isTasksLoading || !areCompleted;

  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const organizationData = useAtomValue(organizationDataAtom);
  const isExternal = campaign?.origin === 'external';
  const isCrossOrg = !!(organizationData?.id && campaign?.organization_id && campaign.organization_id !== organizationData.id);
  const isCrossAccount = !isCrossOrg && !!(googleAdsAccount?.googleads_account_id && campaign?.googleads_account?.googleads_account_id && campaign.googleads_account.googleads_account_id !== googleAdsAccount.googleads_account_id);
  const isArchived = campaign?.is_deleted === true;
  const isReadOnly = isExternal || isCrossOrg || isCrossAccount || isArchived;
  if (isCrossOrg || isCrossAccount) return null;

  const handleStopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div className={className}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {variant === 'header' ? (
            <Button
              variant='outline'
              className='h-9 gap-2'
              onClick={handleStopPropagation}
            >
              Campaign
              <ChevronDown className='h-4 w-4' />
            </Button>
          ) : (
            <Button
              variant='ghost'
              className='h-8 w-8 p-0 hover:bg-slate-100 rounded-full'
              onClick={handleStopPropagation}
            >
              <span className='sr-only'>Campaign actions</span>
              <MoreHorizontal className='w-4 h-4 text-slate-500' />
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align='end'
          className='min-w-[180px] shadow-lg rounded-lg border border-slate-100'
        >
          {/* Pause Campaign — only shown when live; resume is handled by the header button */}
          {states.isPauseResumeText === 'Pause' && (
            <DropdownMenuItem
              className={`cursor-pointer px-3 py-2 text-sm hover:bg-slate-50 ${
                isReadOnly || isStillGenerating || states.isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              onClick={(e) => {
                handleStopPropagation(e);
                if (!isReadOnly && !isStillGenerating && !states.isLoading) {
                  actions.handleTogglePause();
                }
              }}
              disabled={isReadOnly || isStillGenerating || states.isLoading}
            >
              {states.isLoading ? (
                <Spinner size="sm" className="shrink-0 mr-2 text-amber-500" />
              ) : (
                <Pause className='h-4 w-4 shrink-0 mr-2 text-slate-600' />
              )}
              {states.isLoading ? 'Pausing...' : 'Pause Campaign'}
            </DropdownMenuItem>
          )}

          {/* Add Ad */}
          {onNewAd && (
            <DropdownMenuItem
              className={`cursor-pointer px-3 py-2 text-sm hover:bg-slate-50 ${
                newAdDisabled || isStillGenerating ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              onClick={(e) => {
                handleStopPropagation(e);
                if (!newAdDisabled && !isStillGenerating) {
                  onNewAd();
                }
              }}
              disabled={newAdDisabled || isStillGenerating}
            >
              <Plus className='h-4 w-4 shrink-0 mr-2 text-slate-600' />
              Add Ad
            </DropdownMenuItem>
          )}

          {/* View in Google Ads */}
          <DropdownMenuItem
            className='cursor-pointer px-3 py-2 text-sm hover:bg-slate-50'
            onClick={(e) => {
              handleStopPropagation(e);
              if (viewInGoogleAdsAction && !viewInGoogleAdsAction.disabled) {
                viewInGoogleAdsAction.onClick();
              } else if (states.hasGoogleAdsConnection) {
                actions.handleViewInGoogleAds();
              }
            }}
            disabled={viewInGoogleAdsAction?.disabled || !states.hasGoogleAdsConnection}
            title={
              viewInGoogleAdsAction?.disabled
                ? viewInGoogleAdsAction.disabledReason
                : !states.hasGoogleAdsConnection
                  ? 'Google Ads account not connected'
                  : undefined
            }
          >
            <ExternalLink className='h-4 w-4 shrink-0 mr-2 text-slate-600' />
            View in Google Ads
          </DropdownMenuItem>

          {/* Transfer Campaign */}
          <DropdownMenuItem
            className='cursor-pointer px-3 py-2 text-sm hover:bg-slate-50'
            onClick={(e) => {
              handleStopPropagation(e);
              if (onTransferCampaign) {
                onTransferCampaign();
              }
            }}
            disabled={isReadOnly || isStillGenerating || !!campaign?.googleads_campaign?.googleads_campaign_id}
            title={
              isReadOnly
                ? 'External campaign'
                : isStillGenerating
                  ? 'Cannot transfer while generating'
                  : campaign?.googleads_campaign?.googleads_campaign_id
                    ? 'Cannot transfer campaign that has been created in Google Ads'
                    : 'Transfer campaign to different Google Ads account'
            }
          >
            <ArrowRightLeft className='h-4 w-4 shrink-0 mr-2 text-slate-600' />
            Transfer Campaign
          </DropdownMenuItem>

          {/* Create Copy */}
          <DropdownMenuItem
            onClick={(e) => {
              handleStopPropagation(e);
              if (!isReadOnly && !isStillGenerating) {
                actions.handleCopyCampaign();
              }
            }}
            disabled={isReadOnly || isStillGenerating}
            className={`cursor-pointer px-3 py-2 text-sm hover:bg-slate-50 ${
              isReadOnly || isStillGenerating ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <Copy className='h-4 w-4 shrink-0 mr-2 text-slate-600' />
            Copy Campaign
          </DropdownMenuItem>

          {/* Archive / Restore Campaign */}
          {isArchived ? (
            <DropdownMenuItem
              // Keep the menu open (preventDefault) so the "Restoring..." spinner stays visible
              // until navigation; mirrors the Pause / Archive loading affordance.
              onSelect={async (e) => {
                e.preventDefault();
                if (isRestoring) return;
                setIsRestoring(true);
                try {
                  const { restoreCampaign } = await import('@/lib/apis');
                  await restoreCampaign(campaign.id);
                  await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
                  await queryClient.invalidateQueries({ queryKey: ['campaign', campaign.slug] });
                  navigate({ to: `/campaigns/google/${campaign.slug}` });
                } catch (err) {
                  console.error('[Restore] failed:', err);
                  toast({ description: 'Failed to restore campaign', variant: 'destructive' });
                  setIsRestoring(false);
                }
              }}
              disabled={isRestoring}
              className={`cursor-pointer px-3 py-2 text-sm hover:bg-slate-50 ${
                isRestoring ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isRestoring ? (
                <Spinner size="sm" className="shrink-0 mr-2 text-slate-600" />
              ) : (
                <RotateCcw className='h-4 w-4 shrink-0 mr-2 text-slate-600' />
              )}
              {isRestoring ? 'Restoring...' : 'Restore Campaign'}
            </DropdownMenuItem>
          ) : (campaign?.state?.toLowerCase() === 'paused' || campaign?.state?.toLowerCase() === 'draft') && (
            <DropdownMenuItem
              onClick={(e) => {
                handleStopPropagation(e);
                setShowArchiveConfirm(true);
              }}
              disabled={isReadOnly || isStillGenerating}
              className={`cursor-pointer px-3 py-2 text-sm hover:bg-slate-50 ${
                isReadOnly || isStillGenerating ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <Archive className='h-4 w-4 shrink-0 mr-2 text-slate-600' />
              Archive Campaign
            </DropdownMenuItem>
          )}

          {/* Export Campaign */}
          <DropdownMenuItem
            className='cursor-pointer px-3 py-2 text-sm hover:bg-slate-50'
            onClick={(e) => {
              handleStopPropagation(e);
              downloadCampaignMarkdown(campaign);
            }}
          >
            <Download className='h-4 w-4 shrink-0 mr-2 text-slate-600' />
            Export Campaign
          </DropdownMenuItem>

          {/* Delete Campaign */}
          <DropdownMenuItem
            onClick={(e) => {
              handleStopPropagation(e);
              if (!isReadOnly && !isStillGenerating) {
                actions.handleDeleteCampaign();
              }
            }}
            disabled={isReadOnly || isStillGenerating}
            className={`cursor-pointer px-3 py-2 text-sm hover:bg-slate-50 text-red-600 ${
              isReadOnly || isStillGenerating ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <Trash2 className='h-4 w-4 shrink-0 mr-2' />
            Delete Campaign
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConformationModal
        isOpen={showArchiveConfirm}
        onClose={() => setShowArchiveConfirm(false)}
        onConfirm={async () => {
          setIsArchiving(true);
          try {
            setShowArchiveConfirm(false);
            const { archiveCampaign } = await import('@/lib/apis');
            await archiveCampaign(campaign.id);
            // Invalidate campaign list first so /campaigns index picks the right next campaign
            await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
            // Navigate to campaigns index (auto-redirects to first available campaign)
            navigate({ to: '/campaigns' });
            // Remove stale campaign cache after navigation has settled
            setTimeout(() => {
              queryClient.removeQueries({ queryKey: ['campaign', campaign.slug] });
              setIsArchiving(false);
            }, 300);
          } catch {
            setIsArchiving(false);
            toast({ description: 'Failed to archive campaign', variant: 'destructive' });
          }
        }}
        title="Archive Campaign"
        description={`Are you sure you want to archive "${campaign.name}"? The campaign will be removed from active lists and its budget will be freed. You can restore it later.`}
        confirmText="Archive"
        loadingText="Archiving..."
        isLoading={isArchiving}
      />
    </div>
  );
};
